import {
  DO_NOT_CONTACT_REJECTION_REASON,
  type EmployerProDoNotContact,
  type JobApplicant,
  JobApplicantStatus,
} from '@factoryfixinc/ats-interfaces';

import { DoNotContactPersistence } from './do-not-contact.persistence';
import type { DoNotContactActor, DoNotContactSummary } from './do-not-contact.type';
import type { ProUserProfileWithRelations } from '../pro-profile/pro-profile';
import { useConversationProProfileStore } from '../pro-profile/pro-profile.store';
import ProProfileService from '../pro-profile/pro-profile.service';
import ConversationIndexService from '../conversation-index/conversation-index.service';
import { SearchService } from '@/core/sourcing/search/search.service';
import MeService from '@/core/shared/me/me.service';
import ProNotesService from '@/core/shared/pro-notes/pro-notes.service';
import ProjectService from '@/core/shared/project/project.service';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';

/** Statuses the backend mark cascade rejects out of (ff-rest `dncCascadeActiveStatuses`). */
export const DNC_CASCADE_ACTIVE_STATUSES: ReadonlySet<JobApplicantStatus> = new Set([
  JobApplicantStatus.NEW,
  JobApplicantStatus.CONTACT,
  JobApplicantStatus.CLIENT,
  JobApplicantStatus.REVIEW,
  JobApplicantStatus.INTERVIEW,
  JobApplicantStatus.OFFER,
]);

const TRACKING_SOURCE = 'Conversation Top Banner';
export type DoNotContactSource = typeof TRACKING_SOURCE | 'Talent Search' | 'Candidate Archive';

type CascadedApplication = {
  id: JobApplicant['id'];
  jobId: number;
  status: JobApplicantStatus;
};

export default class DoNotContactService {
  private persistence = new DoNotContactPersistence();
  private store = useConversationProProfileStore();
  private meService = new MeService();
  private proProfileService = new ProProfileService();
  private proNotesService = new ProNotesService();
  private conversationIndexService = new ConversationIndexService();
  private projectService = new ProjectService();

  public get isSelectedProDoNotContact(): boolean {
    const proId = this.store.selectedPro?.id;
    const employerId = this.meService.employer?.id;
    return (
      !!proId &&
      !!employerId &&
      this.isStillSelected(proId, employerId) &&
      !!this.store.selectedPro?.doNotContact?.active
    );
  }

  /**
   * Marks the selected pro and updates local state after the POST succeeds.
   * Resolves `false` when the mark fails and shows an error snackbar.
   */
  public async markSelectedPro(rawReason: string): Promise<boolean> {
    const employerId = this.meService.employer?.id;
    const selectedPro = this.store.selectedPro;
    if (!employerId || !selectedPro) return false;

    // Captured up front: the request can outlive the selection.
    const userProfileId = selectedPro.id;
    if (!this.isStillSelected(userProfileId, employerId)) return false;
    const proPreferenceId = selectedPro.pro?.id;
    const selectedConversation = this.conversationIndexService.selectedConversationIndex;
    const conversationId = selectedConversation?.conversationId;
    const projectId = this.projectService.currentProject?.id;
    const cascadedApplications = this.findCascadedApplications(selectedPro.jobApplicants);
    const reason = rawReason.trim();
    const me = this.currentActor();

    let episode: EmployerProDoNotContact;
    try {
      episode = await this.persistence.mark(employerId, userProfileId, reason || undefined);
    } catch (error) {
      ErrorService.captureException(error);
      SnackbarService.critical('Failed to mark Do Not Contact. Please try again.');
      return false;
    }

    this.conversationIndexService.patchDoNotContact(employerId, userProfileId, true);
    new SearchService().patchDoNotContact(employerId, userProfileId, true);
    if (this.isStillSelected(userProfileId, employerId))
      this.store.setSelectedProDoNotContact(userProfileId, {
        active: true,
        createTs: episode.createTs,
        reason: episode.reason ?? null,
        createdBy: episode.createdByUserProfileId === me?.id ? me : null,
      });

    if (reason && proPreferenceId && episodeMatchesSubmission(episode, me?.id, reason)) {
      try {
        await this.proNotesService.createDoNotContactReasonNote(proPreferenceId, reason);
      } catch (error) {
        ErrorService.captureException(error);
        SnackbarService.critical('Marked Do Not Contact, but the reason note could not be saved.');
      }
    }

    await this.refreshAfterMark({
      employerId,
      userProfileId,
      conversationId,
      projectId,
      cascadedApplications,
    });

    TrackingService.trackAction(TrackingActionName.DO_NOT_CONTACT_MARKED, {
      source: TRACKING_SOURCE,
      has_reason: !!reason,
      cascaded_applications: cascadedApplications.length,
    });
    return true;
  }

  public getSummary(
    employerId: number,
    userProfileId: number,
  ): Promise<DoNotContactSummary | null> {
    return this.persistence.getSummary(employerId, userProfileId);
  }

  /** Closes the active episode. Applications stay rejected: undo never un-rejects. */
  public async unmarkSelectedPro(): Promise<boolean> {
    const employerId = this.meService.employer?.id;
    const selectedPro = this.store.selectedPro;
    if (!employerId || !selectedPro) return false;

    const userProfileId = selectedPro.id;
    if (!this.isStillSelected(userProfileId, employerId)) return false;

    return this.unmarkPro(employerId, userProfileId, TRACKING_SOURCE);
  }

  /** Undo from a list without changing the selected conversation. */
  public async unmarkPro(
    employerId: number,
    userProfileId: number,
    source: DoNotContactSource,
  ): Promise<boolean> {
    if (
      this.meService.employer?.id !== employerId ||
      !Number.isInteger(userProfileId) ||
      userProfileId <= 0
    )
      return false;

    try {
      await this.persistence.unmark(employerId, userProfileId);
    } catch (error) {
      ErrorService.captureException(error);
      SnackbarService.critical('Failed to remove Do Not Contact. Please try again.');
      return false;
    }

    this.conversationIndexService.patchDoNotContact(employerId, userProfileId, false);
    new SearchService().patchDoNotContact(employerId, userProfileId, false);
    if (this.isStillSelected(userProfileId, employerId)) {
      this.store.setSelectedProDoNotContact(userProfileId, null);
      await this.conversationIndexService.refreshSelectedProMessageStatus(true);
    }

    TrackingService.trackAction(TrackingActionName.DO_NOT_CONTACT_REMOVED, {
      source,
    });
    return true;
  }

  private findCascadedApplications(
    jobApplicants: JobApplicant[] | undefined,
  ): CascadedApplication[] {
    return (jobApplicants ?? [])
      .filter(
        (application): application is JobApplicant & { status: JobApplicantStatus } =>
          !!application.status && DNC_CASCADE_ACTIVE_STATUSES.has(application.status),
      )
      .map(({ id, jobId, status }) => ({ id, jobId: Number(jobId), status }));
  }

  private currentActor(): DoNotContactActor | null {
    const me = this.meService.userProfile;
    if (!me) return null;
    const { id, nameFirst, nameLast, avatarUrl } = me;
    return { id, nameFirst, nameLast, avatarUrl };
  }

  /**
   * Pulls the authoritative state (summary with the real actor, cascaded
   * statuses, notes) and mirrors the cascade onto the list row, funnel counts
   * and status history. Every step is best effort and skipped once the user has
   * moved to another candidate.
   */
  private async refreshAfterMark({
    employerId,
    userProfileId,
    conversationId,
    projectId,
    cascadedApplications,
  }: {
    employerId: number;
    userProfileId: number;
    conversationId?: number;
    projectId?: number;
    cascadedApplications: CascadedApplication[];
  }): Promise<void> {
    if (!this.isStillSelected(userProfileId, employerId)) return;

    let refreshedProfile: ProUserProfileWithRelations | undefined;
    try {
      refreshedProfile = await this.proProfileService.getProProfile(
        employerId,
        userProfileId,
        undefined,
        {
          preserveSelectionOnError: true,
        },
      );
      if (!refreshedProfile) return;
    } catch (error) {
      ErrorService.captureException(error);
      if (!this.isStillSelected(userProfileId, employerId)) return;
      // The mark committed; mirror its cascade if the profile could not be refreshed.
      this.store.patchApplicationStatuses(
        cascadedApplications.map(({ id }) => id),
        {
          status: JobApplicantStatus.REJECTED,
          rejectionReason: DO_NOT_CONTACT_REJECTION_REASON,
        },
      );
      for (const { jobId, status } of cascadedApplications) {
        this.projectService.updateLocalProjectJobApplicationStatusCount({
          jobId,
          newStatus: JobApplicantStatus.REJECTED,
          previousStatus: status,
        });
      }
    }
    if (!this.isStillSelected(userProfileId, employerId)) return;

    const applications =
      refreshedProfile?.jobApplicants ??
      cascadedApplications.map(({ id }) => ({
        id,
        status: JobApplicantStatus.REJECTED,
      }));
    if (conversationId) {
      for (const { id, status } of applications) {
        if (status) {
          this.conversationIndexService.patchConversationApplicationStatus(
            conversationId,
            id,
            status,
          );
        }
      }
    }

    if (refreshedProfile && projectId) {
      try {
        await this.projectService.getProjectById(projectId, { select: false });
      } catch (error) {
        ErrorService.captureException(error);
      }
    }
    if (!this.isStillSelected(userProfileId, employerId)) return;

    try {
      await this.proProfileService.getApplicantStatusHistory();
    } catch (error) {
      ErrorService.captureException(error);
    }
  }

  private isStillSelected(userProfileId: number, employerId: number): boolean {
    return (
      this.meService.employer?.id === employerId &&
      (this.store.selectedProEmployerId === undefined ||
        this.store.selectedProEmployerId === employerId) &&
      (!this.conversationIndexService.selectedConversationIndex?.proId ||
        this.conversationIndexService.selectedConversationIndex.proId === userProfileId) &&
      this.store.selectedPro?.id === userProfileId &&
      (this.store.requestedProId === null || this.store.requestedProId === userProfileId)
    );
  }
}

/**
 * The mark endpoint is idempotent and returns the pre-existing episode when the
 * pair was already marked. Only save a note when the episode matches this
 * submission's actor and reason. A match cannot establish whether the episode
 * is new without a server-side created flag, so an identical note may be duplicated.
 */
function episodeMatchesSubmission(
  episode: EmployerProDoNotContact,
  currentUserProfileId: number | undefined,
  reason: string,
): boolean {
  return (
    episode.createdByUserProfileId === currentUserProfileId && (episode.reason ?? '') === reason
  );
}
